/**
 * Billing error messages, keyed by what actually went wrong.
 *
 * One place rather than per-call-site, because the same five statuses come back from four endpoints
 * and the useful message depends on which operation was attempted. "Something went wrong" is never
 * an option here: every failure below either tells the admin what to change or tells them not to
 * retry, and on a page that charges cards the difference matters.
 */

// `changePlan` reprices a live subscription. It shares `checkout`'s copy for everything except the
// ambiguous-failure case below, where the distinction matters: a lost connection during a plan
// change may have left the subscription repriced AND invoiced, so the advice is to re-read state
// rather than retry.
type BillingOperation = 'checkout' | 'topup' | 'portal' | 'cancel' | 'resume' | 'changePlan';

interface AxiosLikeError {
  response?: {
    status?: number;
    // All three shapes, because the API's envelope is not something the frontend gets to decide and
    // reading only one of them silently degrades to generic copy the day it changes.
    data?: { detail?: string; error?: string; message?: string };
  };
  message?: string;
  code?: string;
}

/**
 * The server's own message, when it sent one.
 *
 * Passed through only for 409 and 422 — the two statuses that mean "your request was understood and
 * rejected for a specific, fixable reason", where the server's wording is genuinely better than
 * anything guessable here. Every other status keeps fixed copy: relaying an arbitrary 5xx body to an
 * admin risks putting backend internals on screen, and it would not help them anyway.
 */
function serverMessage(error: unknown): string | null {
  const data = (error as AxiosLikeError)?.response?.data;
  const text = data?.detail ?? data?.error ?? data?.message;
  if (typeof text !== 'string' || !text.trim()) return null;
  // A defensive cap: a message long enough to be a stack trace is not a message for a person.
  return text.trim().length > 300 ? null : text.trim();
}

function statusOf(error: unknown): number | undefined {
  return (error as AxiosLikeError)?.response?.status;
}

/**
 * True when the request never got an answer, so whether it took effect is unknown.
 *
 * This is the case that must never be auto-retried on a top-up: the charge may already have
 * happened, and the endpoint has no idempotency key to make a second attempt safe.
 */
export function isAmbiguousFailure(error: unknown): boolean {
  const candidate = error as AxiosLikeError;
  if (statusOf(error) !== undefined) return false;
  return (
    candidate?.code === 'ECONNABORTED' ||
    candidate?.code === 'ERR_NETWORK' ||
    candidate?.code === 'ETIMEDOUT' ||
    /network|timeout/i.test(candidate?.message ?? '')
  );
}

export function billingErrorMessage(error: unknown, operation: BillingOperation): string {
  if (isAmbiguousFailure(error)) {
    if (operation === 'topup') {
      // Deliberately does not offer a retry. The charge may have gone through.
      return "We lost the connection before Stripe answered, so this top-up may or may not have been charged. Refresh the billing status before trying again.";
    }
    if (operation === 'changePlan') {
      // Same hazard as a top-up: an upgrade invoices the prorated difference immediately, so a
      // retry after a lost connection can bill twice.
      return 'We lost the connection before Stripe answered, so this plan change may or may not have been applied. Refresh the billing status before trying again.';
    }
    return 'We lost the connection before Stripe answered. Refresh to see the current state.';
  }

  const status = statusOf(error);

  switch (status) {
    case 401:
      // The axios interceptor already handles the refresh-then-redirect path, so if a 401 surfaces
      // here the session is genuinely gone.
      return 'Your session has expired. Sign in again to manage billing.';

    case 403:
      return 'Only an organization owner or admin can manage billing.';

    case 409:
      // The one status with a specific, actionable remedy — and the server's own copy says it well.
      return (
        serverMessage(error) ??
        'This seat already has an active subscription. Cancel it first, then buy a new plan once the current period ends.'
      );

    case 422:
      // Validation: the member is not in the org, or the plan/amount was rejected. The server names
      // which, so pass it through rather than guessing.
      return serverMessage(error) ?? 'That request was rejected. Check the member and try again.';

    case 502:
      if (operation === 'cancel') {
        // 502 covers both "no subscription to cancel" and "Stripe is unreachable" on this endpoint,
        // so the message must not assert an outage it cannot confirm.
        return "We couldn't cancel this subscription. It may have already ended, or the billing service may be unavailable — refresh to check.";
      }
      return operation === 'topup'
        ? UNKNOWN_CHARGE
        : 'The billing service is unavailable right now. Nothing was charged. Try again in a few minutes.';

    default:
      return operation === 'topup'
        ? UNKNOWN_CHARGE
        : 'That did not go through. Nothing was charged.';
  }
}

/**
 * The only honest copy for a top-up that failed in a way we cannot classify.
 *
 * "Nothing was charged" is a claim about Stripe, and on this endpoint we are not in a position to
 * make it: a 502 or an unrecognised 5xx can arrive after the charge was created, and there is no
 * idempotency key that would make a retry safe. Telling an admin nothing happened when something may
 * have is the one error message on this screen that can cost them money, so the unclassified cases
 * point at the billing status instead of guessing.
 */
const UNKNOWN_CHARGE =
  "The billing service didn't complete this top-up. Refresh the billing status to see whether the charge went through before trying again.";
